import { Injectable } from '@angular/core';

import {
    CompactType,
    DisplayGrid,
    Gridster,
    GridsterConfig,
    GridType,
} from 'angular-gridster2';
import { debounce } from 'underscore';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';

import { dashboardsBlockTypes } from '../../Reports/models/reports-block-types.model';

import { AbstractPageBuilderService } from '../../PageBuilder/services/abstract-page-builder.service';
import { StylesService } from '../../Styles/services/styles.service';
import { TwigRendererService } from '../../PageBuilder/services/twig-renderer.service';
import { BlockModel } from '../../PageBuilder/models/block.model';

import { DashboardChartBlockComponent } from '../components/DashboardChartBlock/dashboard-chart-block.component';
import { DashboardService } from './dashboard.service';
import { ReportBlockTypeEnum } from '../../../Common/Reports/enums/report-block-type.enum';
import {
    dashboardChartFixedCellHeight,
    dashboardChartFixedCellWidth,
    dashboardChartMargin,
    dashboardColumnNumber
} from '@Common/Charts/helpers/chart-size.helper';
import { DashboardModel } from '../../../Common/Dashboard/models/dashboard.model';


@Injectable()
export class DashboardPageBuilderService extends AbstractPageBuilderService {

    private blockChartMap: Map<BlockModel, DashboardChartBlockComponent>;
    public gridsterAPI;

    readonly gridSettings: GridsterConfig = {
        gridType: GridType.VerticalFixed,
        compactType: CompactType.None,
        displayGrid: DisplayGrid.OnDragAndResize,
        margin: dashboardChartMargin,
        outerMargin: true,
        outerMarginTop: 0,
        outerMarginRight: 1,
        outerMarginBottom: 30, // seems to be getting ignored
        outerMarginLeft: 0,
        useTransformPositioning: true,
        mobileBreakpoint: 640,
        minCols: dashboardColumnNumber, // always be 4 columns wide
        maxCols: dashboardColumnNumber,
        minRows: 1,
        maxRows: 100,
        maxItemCols: 4,
        minItemCols: 1,
        maxItemRows: 4,
        minItemRows: 1,
        maxItemArea: 16, // max ex: 4x4 per item
        minItemArea: 1,
        defaultItemCols: 1,
        defaultItemRows: 1,
        fixedColWidth: dashboardChartFixedCellWidth,
        fixedRowHeight: dashboardChartFixedCellHeight,
        addEmptyRowsCount: 0,
        keepFixedHeightInMobile: false,
        keepFixedWidthInMobile: false,
        scrollSensitivity: 10,
        scrollSpeed: 20,
        enableEmptyCellClick: false,
        enableEmptyCellContextMenu: false,
        enableEmptyCellDrop: true,
        enableEmptyCellDrag: true,
        enableOccupiedCellDrop: true,
        emptyCellDragMaxCols: 50,
        emptyCellDragMaxRows: 50,
        ignoreMarginInRow: false,
        swap: true,
        swapWhileDragging: true,
        pushItems: true,
        disablePushOnDrag: false,
        disablePushOnResize: false,
        pushResizeItems: false,
        disableWindowResize: false,
        disableWarnings: false,
        scrollToNewItems: false,
        pushDirections: {
            north: true,
            east: true,
            south: true,
            west: true
        },
        draggable: {
            enabled: true,
            dragHandleClass: 'agpr-chartBlock-dragHandle',
            ignoreContent: true
        },
        resizable: {
            enabled: true
        },
        initCallback: (gridster) => this.getGridsterAPI(gridster),

        /**
         * callback to call for each item when is changes x, y, rows, cols.
         * can accept gridsterItem, gridsterItemComponent as params (default of gridster)
         *
         * saves the chart everytime your drag or resize
         *
         * this is called for EVERY block that has moved, so if the user drags a block,
         * and another blocks gets moved from the action, this function will be called twice.
         *
         * NOTE: we are pretty limited with what gridster gives us
         */
        itemChangeCallback: debounce((blockModel) => {
            this.dashboardService.saveCurrentDashboard(blockModel?.displayElement);
        }, 1000),
    };

    constructor(
        public dashboardService: DashboardService,
        public stylesService: StylesService,
        public twigRendererService: TwigRendererService,
        public localizationService: LocalizationService,
    ) {
        super(stylesService, twigRendererService, localizationService);

        this.blockChartMap = new Map();
        this.setBlockTypes(dashboardsBlockTypes);
    }

    setCurrentDashboard(currentDashboard: DashboardModel): void {
        if (!currentDashboard) {
            return;
        }

        currentDashboard.blocks.forEach(block => this.setMaxMinDimensions(block));

        // Don't call this.setBlocks because it calls setCurrentDashboardBlocks and results in a infinite loop
        super.setBlocks(currentDashboard.blocks);
    }

    /**
     * Removes a block from the current list
     *
     * @param block The block to remove
     * also passed the deleted block, so we can put the name in the growl message
     */
    removeBlock(block: BlockModel, position?: string): void {
        const index = this.blocks.indexOf(block);

        if (index < 0) {
            // shouldn't ever happen
            throw new Error('Item not in list');
        }

        // If the deleted block is the selected block, unselect it
        if (this.selectedBlock?.displayElement?.isEqual(block.displayElement)) {
            this.deselectBlock(false);
        }
        const blockRemoved = this.blocks.splice(index, 1);

        this.setBlocks(this.blocks, blockRemoved?.[0]?.displayElement);
    }

    /**
     * Replaces a block with another
     * also passed the updated block, so we can put the name in the growl message
     */
    replaceBlock(oldBlock: BlockModel, newBlock: BlockModel): void {
        const blocks = this.blocks;
        const index = blocks.indexOf(oldBlock);
        blocks[index] = newBlock;
        this.setBlocks(this.blocks, newBlock?.displayElement);
        super.selectedBlockUpdated();
    }

    /**
     * Updates the list of blocks
     */
    setBlocks(blocks: BlockModel[], lastChartTouched?: any): void {
        super.setBlocks(blocks);

        this.dashboardService.setCurrentDashboardBlocks(this.blocks, lastChartTouched);
    }

    /**
     * These maps are to give export access to the charts directly by iterating over the BlockModels, this is the setter
     */
    public mapBlockToChartComponent(block: BlockModel, chartBlock: DashboardChartBlockComponent): void {
        this.blockChartMap.set(block, chartBlock);
    }

    /**
     * These maps are to give export access to the charts directly by iterating over the BlockModels, this is the getter
     */
    public getChartComponentFromBlock(block: BlockModel): DashboardChartBlockComponent {
        return this.blockChartMap.get(block);
    }

    /**
     * Updates the blocks when changes occur so that things are notified
     * @NOTE Called by the BlockForm component when a form field value is changed
     */
    formFieldUpdated(): void {
        // using super here prevents the reports service from causing a reset in the selected block
        super.setBlocks(this.blocks);
        this.notifyBlockDisplayElementChanged(this.selectedBlock);
    }

    setMaxMinDimensions(block: BlockModel) {
        // Default values are 1x1 minimum and 4x4 maximum. We only have to set values that differ from those.
        switch(block.type.key) {
            case ReportBlockTypeEnum.COVERAGE_SUMMARY:
                block.maxItemCols = 2;
                block.maxItemRows = 2;
                break;
            case ReportBlockTypeEnum.PROMINENCE_BY_REACH:
            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME:
                block.minItemCols = 2;
                block.minItemRows = 2;
                break;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                block.minItemRows = 2;
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.TOP_POSTS:
                block.minItemCols = 2;
                break;
        }

        if (block.maxItemRows && block.rows > block.maxItemRows) {
            block.rows = block.maxItemRows;
        } else if (block.minItemRows && block.rows < block.minItemRows) {
            block.rows = block.minItemRows;
        }

        if (block.maxItemCols && block.cols > block.maxItemCols) {
            block.cols = block.maxItemCols;
        } else if (block.minItemCols && block.cols < block.minItemCols) {
            block.cols = block.minItemCols;
        }
    }

    getGridsterAPI(gridster: Gridster) {
        this.gridsterAPI = gridster.api;
    }
}
